import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';
import {
  login as loginService,
  logout as logoutService,
  onAuthStateChange,
  fetchUserProfile,
} from '../services/auth';
import {
  subscribeToOrganizationAwaitingPayment,
  subscribeToOrganizationPaused,
} from '../services/api';
import type { User, LoginRequest, AuthError } from '../types/auth';
import { SentryService } from '../services/sentry';
import { clearAllTeamChips } from '../utils/teamChipsStorage';
import { clearAllScheduleScopes } from '../utils/scheduleScopeStorage';
import { clearAllPageSizes } from '../utils/pageSizeStorage';
import { clearAllColumnLayouts } from '../utils/playersTableColumnStorage';
import { clearGetStartedChecklistCache } from '../services/onboarding';
import { clearSignup } from '../features/signup/signupStorage';

export type ActiveOrganization = {
  id: string;
  name: string;
  sport: string | null;
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  initializing: boolean;
  error: AuthError | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  // System admin org switching
  activeOrganization: ActiveOrganization | null;
  isSystemAdmin: boolean;
  isImpersonating: boolean;
  switchOrganization: (org: ActiveOrganization) => void;
  clearImpersonation: () => void;
  // The effective org ID being used for API calls (for use as a dependency)
  effectiveOrganizationId: string | null;
  // Organization pause (billing suspension) state
  organizationPaused: boolean;
  organizationPausedMessage: string | null;
  /**
   * The organization was created by self-serve signup and has not paid yet. `ProtectedRoute` sends
   * such a user to `/signup/payment` rather than into an app where every request is refused.
   */
  organizationAwaitingPayment: boolean;
  /** Re-fetch /api/auth/me (reachable while paused or awaiting payment) to pick up a change. */
  refreshProfile: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/** A mid-session organization pause detected from a 403 organization_paused. */
type PausedOverride = { message: string | null };

const ACTIVE_ORG_STORAGE_KEY = 'activeOrganizationId';
const ACTIVE_ORG_NAME_STORAGE_KEY = 'activeOrganizationName';
const ACTIVE_ORG_SPORT_STORAGE_KEY = 'activeOrganizationSport';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [activeOrganization, setActiveOrganization] = useState<ActiveOrganization | null>(null);
  /**
   * Set when an in-flight API call is rejected with a 403 organization_paused,
   * i.e. the org was paused after this session's profile was cached. Null means
   * "no mid-session pause detected"; the profile remains the source of truth.
   * The inner `message` is the server's copy, or null if it sent none.
   */
  const [pausedOverride, setPausedOverride] = useState<PausedOverride | null>(null);
  /**
   * Set when an API call is rejected with a 403 organization_awaiting_payment, the same way
   * `pausedOverride` is: the cached profile can say "active" while the server says otherwise (an
   * older profile, or a profile-less fallback user). Cleared by any fresh profile that is not
   * awaiting payment.
   */
  const [awaitingPaymentOverride, setAwaitingPaymentOverride] = useState(false);

  // Derived values
  const isSystemAdmin = user?.profile?.role === 'system_admin';
  const isImpersonating = isSystemAdmin && activeOrganization !== null &&
    activeOrganization.id !== user?.profile?.organizationId;
  // The effective org ID - what API calls will use
  const effectiveOrganizationId = activeOrganization?.id || user?.profile?.organizationId || null;

  // Paused state: the profile from /api/auth/me, or a mid-session 403 override.
  // A profile-less user (offline fallback) reads as not paused, and system admins
  // are exempt from pausing entirely (the API never 403s them either).
  const organizationPaused =
    !isSystemAdmin &&
    (user?.profile?.organizationPaused === true || (user !== null && pausedOverride !== null));
  const organizationPausedMessage =
    pausedOverride?.message ?? user?.profile?.organizationPausedMessage ?? null;

  // Awaiting payment: same derivation as the pause. System admins are exempt (the API never
  // refuses them) and a signed-out session never awaits anything.
  const organizationAwaitingPayment =
    !isSystemAdmin &&
    (user?.profile?.organizationAwaitingPayment === true ||
      (user !== null && awaitingPaymentOverride));

  // React to an organization being paused mid-session
  useEffect(() => {
    return subscribeToOrganizationPaused((message) => {
      setPausedOverride((current) =>
        current && current.message === message ? current : { message }
      );
    });
  }, []);

  // React to a request refused because the organization has not paid yet
  useEffect(() => {
    return subscribeToOrganizationAwaitingPayment(() => {
      setAwaitingPaymentOverride(true);
    });
  }, []);

  // Load persisted active organization on mount
  useEffect(() => {
    const storedOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    const storedOrgName = localStorage.getItem(ACTIVE_ORG_NAME_STORAGE_KEY);
    const storedOrgSport = localStorage.getItem(ACTIVE_ORG_SPORT_STORAGE_KEY);

    if (storedOrgId && storedOrgName) {
      setActiveOrganization({
        id: storedOrgId,
        name: storedOrgName,
        sport: storedOrgSport || null,
      });
    }
  }, []);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setInitializing(false);

      // A freshly fetched profile is authoritative - drop any stale pause override
      // (e.g. the org was resumed since the 403 that set it). Only a user that
      // actually carries a profile counts: the profile-less fallback user built
      // when /api/auth/me fails transiently says nothing about pause state, and
      // must not clear a pause we already know about.
      if (user === null || (user.profile != null && user.profile.organizationPaused !== true)) {
        setPausedOverride(null);
      }
      // Same rule for awaiting payment: only a real profile that says "paid" clears it.
      if (
        user === null ||
        (user.profile != null && user.profile.organizationAwaitingPayment !== true)
      ) {
        setAwaitingPaymentOverride(false);
      }

      // Set Sentry user context when user logs in or state changes
      if (user) {
        SentryService.setUser({
          id: user.uid,
          email: user.email || undefined,
          role: user.profile?.role,
          orgId: user.profile?.organizationId,
        });
      } else {
        // Clear Sentry user context on logout
        SentryService.clearContext();
      }

      // Clear active organization if user is not a system admin
      if (!user || user.profile?.role !== 'system_admin') {
        setActiveOrganization(null);
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_ORG_NAME_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_ORG_SPORT_STORAGE_KEY);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true);
      setError(null);
      const user = await loginService(credentials);
      setUser(user);

      // Set Sentry user context on successful login
      SentryService.setUser({
        id: user.uid,
        email: user.email || undefined,
        role: user.profile?.role,
        orgId: user.profile?.organizationId,
      });
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      await logoutService();
      setUser(null);
      setPausedOverride(null);
      setAwaitingPaymentOverride(false);

      // Clear Sentry user context on logout
      SentryService.clearContext();

      // Clear all team chips from localStorage
      clearAllTeamChips();

      // Clear the persisted schedule scope so it never leaks across users/tenants
      clearAllScheduleScopes();

      // Same for the players-list page size and column layout
      clearAllPageSizes();
      clearAllColumnLayouts();

      // The Get Started checklist is cached in module scope rather than localStorage, so it
      // outlives the React tree but not the tab. Without this the next user to sign in here reads
      // the previous one's checklist, including whether *they* had hidden it.
      clearGetStartedChecklistCache();

      // An unfinished signup's form lives in localStorage (shared across tabs); it must not be
      // offered to whoever signs in next.
      clearSignup();
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  /**
   * Re-fetch the profile from /api/auth/me. That endpoint stays reachable while
   * an organization is paused, so this is how a user recovers when their org is
   * resumed mid-session (otherwise the stale pause override would persist).
   */
  const refreshProfile = useCallback(async () => {
    const refreshedUser = await fetchUserProfile();
    setUser(refreshedUser);

    if (refreshedUser.profile?.organizationPaused !== true) {
      setPausedOverride(null);
    }
    if (refreshedUser.profile?.organizationAwaitingPayment !== true) {
      setAwaitingPaymentOverride(false);
    }
  }, []);

  const switchOrganization = useCallback((org: ActiveOrganization) => {
    if (!isSystemAdmin) {
      console.warn('Only system admins can switch organizations');
      return;
    }

    setActiveOrganization(org);
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, org.id);
    localStorage.setItem(ACTIVE_ORG_NAME_STORAGE_KEY, org.name);
    if (org.sport) {
      localStorage.setItem(ACTIVE_ORG_SPORT_STORAGE_KEY, org.sport);
    } else {
      localStorage.removeItem(ACTIVE_ORG_SPORT_STORAGE_KEY);
    }

    // The cached checklist belongs to the organization we are leaving. Completion is org-wide and
    // api.ts scopes every request by the impersonation header, but nothing here reloads the page —
    // so without this an admin switching to a brand-new program sees that program's setup reported
    // as already done, using the previous one's answer.
    clearGetStartedChecklistCache();
  }, [isSystemAdmin]);

  const clearImpersonation = useCallback(() => {
    setActiveOrganization(null);
    localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_ORG_NAME_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_ORG_SPORT_STORAGE_KEY);

    // Dropping the impersonation changes the organization just as much as taking one on.
    clearGetStartedChecklistCache();
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    initializing,
    error,
    login,
    logout,
    clearError,
    activeOrganization,
    isSystemAdmin,
    isImpersonating,
    switchOrganization,
    clearImpersonation,
    effectiveOrganizationId,
    organizationPaused,
    organizationPausedMessage,
    organizationAwaitingPayment,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
